const SERVER_MAX_EVENTS_PER_BATCH = 500;
const MAX_FRAME_BYTES = 1.5 * 1024 * 1024;
const SOFT_BUFFER = 80;
const HARD_BUFFER = 200;
const MIN_FLUSH_MS = 100;

/** rrweb EventType */
const EVENT_FULL_SNAPSHOT = 2;
const EVENT_INCREMENTAL = 3;
/** rrweb IncrementalSource */
const SRC_MOUSE_MOVE = 1;
const SRC_TOUCH_MOVE = 6;
const SRC_DRAG = 12;

const MASK_TEXT_SELECTOR = [
    ".gp-private",
    "[data-private]",
    'input[name*="NationalCode"]',
    'input[name*="nationalCode"]',
    'input[name*="Password"]',
    'input[type="password"]',
    'input[name*="Card"]',
    'input[autocomplete="cc-number"]'
].join(", ");

/**
 * Practical presets (user client).
 * InlineStylesheet/RecordCanvas default OFF — both inflate traffic hard.
 * Default session quality is LOW; admin sends CONFIG to raise to MEDIUM/HIGH.
 * HIGH prioritizes low FlushIntervalMs for live feel (latency ≈ flush/2).
 */
const PRESETS = {
    LOW: {
        Preset: "LOW",
        FlushIntervalMs: 900,
        MaxEventsPerBatch: 80,
        MouseMoveThrottleMs: 250,
        ScrollThrottleMs: 200,
        CheckoutEveryNms: 0,
        InlineStylesheet: false,
        RecordCanvas: false
    },
    MEDIUM: {
        Preset: "MEDIUM",
        FlushIntervalMs: 350,
        MaxEventsPerBatch: 200,
        MouseMoveThrottleMs: 150,
        ScrollThrottleMs: 150,
        CheckoutEveryNms: 0,
        InlineStylesheet: false,
        RecordCanvas: false
    },
    HIGH: {
        Preset: "HIGH",
        FlushIntervalMs: 120,
        MaxEventsPerBatch: 400,
        MouseMoveThrottleMs: 50,
        ScrollThrottleMs: 50,
        CheckoutEveryNms: 0,
        InlineStylesheet: false,
        RecordCanvas: false
    }
};

let stopFn = null;
let flushTimer = null;
let buffer = [];
let isLive = false;
let sendEvents = null;
let dropLowPriority = false;
let recordModule = null;
let appliedRecorderKey = null;
let restartToken = 0;
// Start sessions on LOW (slow-network safe); admin raises quality via CONFIG.
let currentSettings = {...PRESETS.LOW};

function isPresent(value) {
    return value !== null && value !== undefined;
}

function clampBatchSize(value, fallback = PRESETS.MEDIUM.MaxEventsPerBatch) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return Math.min(fallback, SERVER_MAX_EVENTS_PER_BATCH);
    return Math.min(Math.floor(n), SERVER_MAX_EVENTS_PER_BATCH);
}

function positiveOr(value, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return fallback;
    return Math.floor(n);
}

function mergeSampling(baseMove, baseScroll, patchSampling, patch) {
    let move = baseMove;
    let scroll = baseScroll;
    if (patchSampling && typeof patchSampling === "object") {
        if (isPresent(patchSampling.mousemove)) {
            move = positiveOr(patchSampling.mousemove, move);
        }
        if (isPresent(patchSampling.scroll)) {
            scroll = positiveOr(patchSampling.scroll, scroll);
        }
    }
    if (isPresent(patch.MouseMoveThrottleMs)) {
        move = positiveOr(patch.MouseMoveThrottleMs, move);
    }
    if (isPresent(patch.ScrollThrottleMs)) {
        scroll = positiveOr(patch.ScrollThrottleMs, scroll);
    }
    return {move, scroll};
}

function mergeSettings(incoming) {
    const patch = incoming && typeof incoming === "object" ? incoming : {};
    const presetName = typeof patch.Preset === "string" ? patch.Preset.toUpperCase() : null;
    const preset = presetName && presetName !== "CUSTOM" ? PRESETS[presetName] : null;
    const base = preset
        ? {...preset}
        : {...currentSettings, Preset: presetName || currentSettings.Preset || "CUSTOM"};

    const {move, scroll} = mergeSampling(
        base.MouseMoveThrottleMs,
        base.ScrollThrottleMs,
        patch.Sampling,
        patch
    );

    const next = {
        Preset: base.Preset,
        FlushIntervalMs: isPresent(patch.FlushIntervalMs)
            ? positiveOr(patch.FlushIntervalMs, base.FlushIntervalMs)
            : base.FlushIntervalMs,
        MaxEventsPerBatch: isPresent(patch.MaxEventsPerBatch)
            ? clampBatchSize(patch.MaxEventsPerBatch, base.MaxEventsPerBatch)
            : clampBatchSize(base.MaxEventsPerBatch, base.MaxEventsPerBatch),
        MouseMoveThrottleMs: move,
        ScrollThrottleMs: scroll,
        CheckoutEveryNms: isPresent(patch.CheckoutEveryNms)
            ? positiveOr(patch.CheckoutEveryNms, base.CheckoutEveryNms)
            : base.CheckoutEveryNms,
        InlineStylesheet: isPresent(patch.InlineStylesheet)
            ? !!patch.InlineStylesheet
            : !!base.InlineStylesheet,
        RecordCanvas: isPresent(patch.RecordCanvas)
            ? !!patch.RecordCanvas
            : !!base.RecordCanvas
    };

    next.MaxEventsPerBatch = clampBatchSize(next.MaxEventsPerBatch, next.MaxEventsPerBatch);
    currentSettings = next;
    return currentSettings;
}

function recorderOptionsKey(settings) {
    return [
        settings.MouseMoveThrottleMs,
        settings.ScrollThrottleMs,
        settings.CheckoutEveryNms,
        settings.InlineStylesheet ? 1 : 0,
        settings.RecordCanvas ? 1 : 0
    ].join("|");
}

function isLowPriority(event) {
    if (event?.type !== EVENT_INCREMENTAL) return false;
    const source = event?.data?.source;
    return source === SRC_MOUSE_MOVE || source === SRC_TOUCH_MOVE || source === SRC_DRAG;
}

function isFullSnapshot(event) {
    return event?.type === EVENT_FULL_SNAPSHOT;
}

/** UTF-8 size estimate without Blob / double full serialize cost. */
function approxBytesOfEvents(events) {
    let chars = 11; // {"Events":}
    for (let i = 0; i < events.length; i++) {
        if (i > 0) chars += 1;
        try {
            chars += JSON.stringify(events[i]).length;
        } catch (_e) {
            chars += 256;
        }
    }
    return chars;
}

function publishBatch(events) {
    if (!isLive || !sendEvents || !events?.length) return;
    const bytes = approxBytesOfEvents(events);
    if (events.length === 1 && bytes > MAX_FRAME_BYTES) {
        console.warn("[CoBrowse][user] drop-oversized-event", {approxBytes: bytes});
        return;
    }
    if (bytes > MAX_FRAME_BYTES && events.length > 1) {
        const mid = Math.max(1, Math.floor(events.length / 2));
        publishBatch(events.slice(0, mid));
        publishBatch(events.slice(mid));
        return;
    }
    const ok = sendEvents(events, bytes);
    dropLowPriority = ok === false;
}

function maxBatchNow() {
    return clampBatchSize(currentSettings.MaxEventsPerBatch, PRESETS.MEDIUM.MaxEventsPerBatch);
}

function enqueue(event) {
    if (!isLive) return;
    const low = isLowPriority(event);
    if (low && (dropLowPriority || buffer.length >= SOFT_BUFFER)) return;
    if (buffer.length >= HARD_BUFFER) {
        if (low) return;
        for (let i = 0; i < buffer.length; i++) {
            if (isLowPriority(buffer[i])) {
                buffer.splice(i, 1);
                break;
            }
        }
        if (buffer.length >= HARD_BUFFER) return;
    }
    buffer.push(event);

    // First FullSnapshot must leave ASAP so admin player can start.
    if (isFullSnapshot(event)) {
        flush();
        return;
    }
    // Don't wait for timer when batch is already full — smaller messages, lower latency.
    if (buffer.length >= maxBatchNow()) {
        flush();
    }
}

function flush() {
    if (!isLive || buffer.length < 1) return;
    const maxBatch = maxBatchNow();
    let batch = buffer.splice(0, maxBatch);
    if (dropLowPriority || buffer.length >= SOFT_BUFFER) {
        const important = batch.filter((e) => !isLowPriority(e));
        if (important.length) batch = important;
    }
    if (!batch.length) return;
    publishBatch(batch);
}

function restartFlushTimer(intervalMs) {
    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }
    if (!isLive) return;
    const ms = Math.max(MIN_FLUSH_MS, positiveOr(intervalMs, currentSettings.FlushIntervalMs));
    flushTimer = setInterval(flush, ms);
}

function buildRecordOptions() {
    const settings = currentSettings;
    const options = {
        emit(event) {
            enqueue(event);
        },
        sampling: {
            mousemove: settings.MouseMoveThrottleMs,
            mouseInteraction: true,
            scroll: settings.ScrollThrottleMs
        },
        slimDOMOptions: true,
        // Heavy by default — only when CONFIG asks for them.
        inlineStylesheet: !!settings.InlineStylesheet,
        recordCanvas: !!settings.RecordCanvas,
        collectFonts: false,
        maskAllInputs: false,
        maskInputOptions: {password: true},
        maskTextSelector: MASK_TEXT_SELECTOR,
        blockClass: "gp-block-cobrowse"
    };
    // 0 / missing = no periodic FullSnapshot (avoids "stutter" from huge frames).
    if (settings.CheckoutEveryNms > 0) {
        options.checkoutEveryNms = settings.CheckoutEveryNms;
    }
    return options;
}

async function ensureRecordFn() {
    if (!recordModule) {
        recordModule = await import("rrweb");
    }
    return recordModule.record || recordModule.default?.record;
}

function stopRecorderOnly() {
    try {
        if (typeof stopFn === "function") stopFn();
    } catch (_e) {
        // ignore
    }
    stopFn = null;
    appliedRecorderKey = null;
}

async function startRecorderInternal({clearBuffer}) {
    const record = await ensureRecordFn();
    if (typeof record !== "function") {
        console.warn("[CoBrowse][user] rrweb.record missing");
        return false;
    }
    if (clearBuffer) buffer = [];
    stopRecorderOnly();
    stopFn = record(buildRecordOptions());
    appliedRecorderKey = recorderOptionsKey(currentSettings);
    return true;
}

async function silentRestartRecorder() {
    if (!isLive) return;
    const token = ++restartToken;
    flush();
    const ok = await startRecorderInternal({clearBuffer: false});
    if (token !== restartToken) return;
    if (!ok) {
        isLive = false;
        if (flushTimer) {
            clearInterval(flushTimer);
            flushTimer = null;
        }
    }
}

export function setCoBrowseSender(fn) {
    sendEvents = fn;
}

export function applyConfig(settings) {
    const prevKey = appliedRecorderKey;
    mergeSettings(settings);
    console.log("[CoBrowse][user] config-applied", {
        live: isLive,
        settings: {...currentSettings}
    });
    if (!isLive) return currentSettings;

    restartFlushTimer(currentSettings.FlushIntervalMs);

    const nextKey = recorderOptionsKey(currentSettings);
    if (prevKey !== nextKey) {
        silentRestartRecorder();
    }
    return currentSettings;
}

export async function startRecording() {
    if (isLive) return;
    // Always open the session on LOW; admin raises quality with CONFIG after START.
    currentSettings = {...PRESETS.LOW};
    isLive = true;
    dropLowPriority = false;
    const ok = await startRecorderInternal({clearBuffer: true});
    if (!ok) {
        isLive = false;
        return;
    }
    restartFlushTimer(currentSettings.FlushIntervalMs);
    // Kick flush once in case FullSnapshot was already emitted synchronously.
    flush();
}

export function stopRecording() {
    isLive = false;
    restartToken += 1;
    dropLowPriority = false;
    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }
    buffer = [];
    stopRecorderOnly();
    currentSettings = {...PRESETS.LOW};
}

export function isRecording() {
    return isLive;
}

export function getCoBrowseSettings() {
    return {...currentSettings};
}
